import { useMemo, useState } from 'react';
import { ChevronDown, Download, Search } from 'lucide-react';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Badge } from '../../components/Badge';
import { Input } from '../../components/Input';
import { Money } from '../../components/Money';
import { PageHeader } from '../../components/PageHeader';
import { Skeleton } from '../../components/Skeleton';
import { formatDate } from '../../lib/format';
import { downloadCsv } from '../../lib/csv';
import { toMajor } from '../../contracts/money';
import { useAdminPayments } from '../../api/admin';
import type { PaymentStatus, PaymentMethod } from '../../contracts/enums';

const STATUS_TONE: Record<PaymentStatus, 'success' | 'warning' | 'error' | 'neutral'> = {
  paid: 'success',
  pending: 'warning',
  failed: 'error',
  refunded: 'neutral',
  partial_refunded: 'neutral',
  disputed: 'error',
};

/** Wire enum → display label for the gateway/method column + filter. */
const METHOD_LABEL: Record<PaymentMethod, string> = {
  sslcommerz: 'SSLCommerz',
  manual_cash: 'Cash',
  bank_transfer: 'Bank Transfer',
  bkash: 'bKash',
  nagad: 'Nagad',
  stripe: 'Stripe',
  paypal: 'PayPal',
};

/** Cross-business payments list. */
export function Payments() {
  const [statusFilter, setStatusFilter] = useState('all');
  const [methodFilter, setMethodFilter] = useState('all');
  const [search, setSearch] = useState('');
  const { data: payments = [], isLoading } = useAdminPayments();

  const filtered = useMemo(() => {
    return payments.filter((p) => {
      if (statusFilter !== 'all' && p.status !== statusFilter) return false;
      if (methodFilter !== 'all' && p.method !== methodFilter) return false;
      if (search && !p.invoiceNumber.includes(search.toUpperCase()) && !p.businessName.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [payments, statusFilter, methodFilter, search]);

  const totalPaid = filtered.filter((p) => p.status === 'paid').reduce((s, p) => s + p.amountMinor, 0);
  const totalPending = filtered.filter((p) => p.status === 'pending').reduce((s, p) => s + p.amountMinor, 0);
  const totalRefunded = filtered.filter((p) => p.status === 'refunded').reduce((s, p) => s + p.amountMinor, 0);

  function exportCsv() {
    downloadCsv(
      'payments',
      ['Invoice', 'Business', 'Reseller', 'Status', 'Method', 'Amount (BDT)', 'Paid'],
      filtered.map((p) => [
        p.invoiceNumber, p.businessName, p.resellerName ?? '', p.status,
        METHOD_LABEL[p.method] ?? p.method, toMajor(p.amountMinor), p.paidAt ? formatDate(p.paidAt) : '',
      ]),
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto">
      <PageHeader
        eyebrow="Platform"
        title="Payments"
        subtitle="Every subscription payment across every business."
        action={<Button variant="secondary" size="sm" iconLeft={<Download className="w-4 h-4" />} onClick={exportCsv} disabled={filtered.length === 0}><span className="hidden sm:inline">Export CSV</span></Button>}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <Card className="p-4 sm:p-5">
          <div className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400">Paid (filtered)</div>
          <div className="mt-1.5 text-xl sm:text-2xl font-semibold">
            <Money minor={totalPaid} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50" />
          </div>
          <div className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{filtered.filter((p) => p.status === 'paid').length} payments</div>
        </Card>
        <Card className="p-4 sm:p-5">
          <div className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400">Pending</div>
          <div className="mt-1.5 text-xl sm:text-2xl font-semibold">
            <Money minor={totalPending} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50" />
          </div>
          <div className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{filtered.filter((p) => p.status === 'pending').length} awaiting</div>
        </Card>
        <Card className="p-4 sm:p-5">
          <div className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400">Failed</div>
          <div className="mt-1.5 text-xl sm:text-2xl font-semibold tabular-nums text-zinc-950 dark:text-zinc-50">
            {filtered.filter((p) => p.status === 'failed').length}
          </div>
          <div className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">need attention</div>
        </Card>
        <Card className="p-4 sm:p-5">
          <div className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400">Refunded</div>
          <div className="mt-1.5 text-xl sm:text-2xl font-semibold">
            <Money minor={totalRefunded} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50" />
          </div>
          <div className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{filtered.filter((p) => p.status === 'refunded').length} refunds</div>
        </Card>
      </div>

      <Card className="overflow-hidden">
        <div className="px-5 sm:px-6 py-3 flex flex-wrap items-center gap-2 border-b border-zinc-200 dark:border-zinc-700">
          <Filter
            label="Status"
            value={statusFilter === 'all' ? 'All' : (statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)).replace(/_/g, ' ')}
            options={[
              { v: 'all', l: 'All' },
              { v: 'paid', l: 'Paid' },
              { v: 'pending', l: 'Pending' },
              { v: 'failed', l: 'Failed' },
              { v: 'refunded', l: 'Refunded' },
              { v: 'partial_refunded', l: 'Partial refunded' },
              { v: 'disputed', l: 'Disputed' },
            ]}
            onChange={setStatusFilter}
          />
          <Filter
            label="Method"
            value={methodFilter === 'all' ? 'All' : METHOD_LABEL[methodFilter as PaymentMethod]}
            options={[
              { v: 'all', l: 'All' },
              { v: 'sslcommerz', l: 'SSLCommerz' },
              { v: 'manual_cash', l: 'Cash' },
              { v: 'bank_transfer', l: 'Bank Transfer' },
            ]}
            onChange={setMethodFilter}
          />
          <div className="ml-auto w-full sm:w-72">
            <Input
              placeholder="Search invoice # or business…"
              prefix={<Search className="w-4 h-4" />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="overflow-x-auto nice-scroll">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
              <tr>
                <th className="text-left font-medium px-5 sm:px-6 py-3">Invoice</th>
                <th className="text-left font-medium px-4 py-3">Business</th>
                <th className="text-left font-medium px-4 py-3 hidden md:table-cell">Reseller</th>
                <th className="text-right font-medium px-4 py-3">Amount</th>
                <th className="text-left font-medium px-4 py-3">Method</th>
                <th className="text-left font-medium px-4 py-3">Status</th>
                <th className="text-left font-medium px-5 sm:px-6 py-3 hidden lg:table-cell">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
              {isLoading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={`sk-${i}`}>
                    <td className="px-5 sm:px-6 py-3"><Skeleton className="h-4 w-24 rounded" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-4 w-40 max-w-full rounded" /></td>
                    <td className="px-4 py-3 hidden md:table-cell"><Skeleton className="h-4 w-24 rounded" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-4 w-20 rounded ml-auto" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-4 w-20 rounded" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-5 w-16 rounded-full" /></td>
                    <td className="px-5 sm:px-6 py-3 hidden lg:table-cell"><Skeleton className="h-4 w-24 rounded" /></td>
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-sm text-zinc-500 dark:text-zinc-400">
                    No payments found.
                  </td>
                </tr>
              ) : (
                filtered.map((p) => (
                <tr key={p.id} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition">
                  <td className="px-5 sm:px-6 py-3 mono text-xs text-zinc-700 dark:text-zinc-200 whitespace-nowrap">
                    {p.invoiceNumber}
                  </td>
                  <td className="px-4 py-3 text-zinc-950 dark:text-zinc-50 font-medium">{p.businessName}</td>
                  <td className="px-4 py-3 text-zinc-600 dark:text-zinc-300 hidden md:table-cell">
                    {p.resellerName ?? <span className="text-zinc-400 dark:text-zinc-500">— Direct</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Money minor={p.amountMinor} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50" />
                  </td>
                  <td className="px-4 py-3 text-zinc-700 dark:text-zinc-200">{METHOD_LABEL[p.method]}</td>
                  <td className="px-4 py-3">
                    <Badge tone={STATUS_TONE[p.status]}>
                      {(p.status.charAt(0).toUpperCase() + p.status.slice(1)).replace(/_/g, ' ')}
                    </Badge>
                  </td>
                  <td className="px-5 sm:px-6 py-3 text-zinc-600 dark:text-zinc-300 tabular-nums hidden lg:table-cell">{p.paidAt ? formatDate(p.paidAt) : '—'}</td>
                </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="px-5 sm:px-6 py-3 border-t border-zinc-200 dark:border-zinc-700 text-xs text-zinc-500 dark:text-zinc-400">
          Showing <span className="tabular-nums">{filtered.length}</span> of <span className="tabular-nums">{payments.length}</span>
        </div>
      </Card>
    </div>
  );
}

function Filter({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Array<{ v: string; l: string }>;
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-200 border border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition"
      >
        <span className="text-zinc-500 dark:text-zinc-400">{label}:</span>
        <span>{value}</span>
        <ChevronDown className="w-3 h-3 text-zinc-400" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute top-full mt-1 left-0 z-20 min-w-[160px] rounded-md border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-900 shadow-md py-1">
            {options.map((o) => (
              <button
                key={o.v}
                type="button"
                onClick={() => {
                  onChange(o.v);
                  setOpen(false);
                }}
                className="block w-full text-left px-3 py-1.5 text-xs text-zinc-700 dark:text-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-800"
              >
                {o.l}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
